import { Avatar, Button, Popover, Select, Space } from "antd"
import { ReactNode, useContext, useEffect, useState } from "react"
import { useCrewQuery, useJobQuery, useVisitQuery } from "./apiQueries"
import EditVisitContext from "./EditVisitContext"
import { getJobId } from "./editVisitModel"
import { enrichCrewMembers } from "./labor"
import TimePicker from "./TimePicker"
import WorkareaSelector from "./WorkareaSelector"
import { AuthenticationContext } from "../../AuthenticationContext"

type FieldProps = {
  label: string
  children: ReactNode
}

const Field = ({ label, children }: FieldProps) => <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
  <span>{label}</span>
  {children}
</div>

const AddCrew = () => {
  const { currentUser } = useContext(AuthenticationContext)
  const [isPopupShowing, setIsPopupShowing] = useState(false)
  const data = useContext(EditVisitContext)
  const visitQuery = useVisitQuery(data.visitId)
  const jobId = getJobId(data, visitQuery.data)
  const jobQuery = useJobQuery(jobId, data.visitId)
  const crewQuery = useCrewQuery()
  const crew = enrichCrewMembers(data.laborChanges, crewQuery.data || [])
  const bulkLabor = data.laborChanges.bulkLabor
  const active = crew.filter(x => x.isActive)
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const timeZone = currentUser?.tenant.timeZoneId || browserTimezone

  useEffect(() => {
    if (jobId && data.laborView.length === 0) {
      setIsPopupShowing(true)
    }
    /* eslint-disable-next-line */
  }, [jobId])

  return <Popover trigger="click"
    open={isPopupShowing}
    onOpenChange={open => setIsPopupShowing(open)}
    placement="topRight"
    content={
      <div style={{ width: 560, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field label="Crew members">
          <Select
            mode="multiple"
            allowClear
            placeholder="Select crew members"
            style={{ width: 320 }}
            value={bulkLabor.selectedCrewMemberIds}
            onChange={ids => data.dispatch({ type: 'SET_SELECTED_CREW_MEMBERS', payload: ids })}
            optionFilterProp="label"
            options={active.map(x => ({ value: x.id, label: x.name, avatar: x.avatar, initials: x.initials }))}
            optionRender={option => <Space>
              <Avatar size="small" src={option.data.avatar}>{option.data.initials}</Avatar>
              {option.data.label}
            </Space>}
          />
        </Field>

        <div style={{ display: 'flex', gap: 12 }}>
          <Field label="Start time">
            <TimePicker value={bulkLabor.startAt} onChange={x => data.dispatch({ type: 'SET_BULK_START_AT', payload: x! })} timeZone={timeZone} />
          </Field>
          <Field label="End time">
            <TimePicker allowBlank value={bulkLabor.endAt} onChange={x => data.dispatch({ type: 'SET_BULK_END_AT', payload: x })} timeZone={timeZone} />
          </Field>
        </div>

        <Field label="Work area">
          <WorkareaSelector
            workareas={jobQuery.data?.workareas ?? []}
            selectedWorkareaId={bulkLabor.workareaId}
            onChange={x => data.dispatch({ type: 'SET_BULK_WORKAREA_ID', payload: x })}
          />
        </Field>

        <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
          <Button type="primary" onClick={() => {
            data.dispatch({ type: 'ADD_CREW_MEMBERS' })
            data.dispatch({ type: 'VISIT_DATE_CHANGED', payload: bulkLabor.startAt })
            setIsPopupShowing(false)
          }}>Add</Button>
        </div>
      </div>
    }
  >
    <Button>Add Crew Members</Button>
  </Popover>
}
export default AddCrew
